/**
 * Find and Replace Tool
 * 
 * Finds and replaces text with support for regex.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const FindReplaceTool: ITransformerTool = {
  id: 'find-replace',
  name: 'Find & Replace',
  description: 'Find and replace text with regex support',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['find', 'replace', 'search', 'regex', 'substitute', 'text'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT, SEMANTIC_TYPES.REGEX],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'find',
      type: 'string',
      label: 'Find',
      defaultValue: '',
      description: 'Text or regex pattern to find',
    },
    {
      name: 'replace',
      type: 'string',
      label: 'Replace',
      defaultValue: '',
      description: 'Replacement text (supports $1, $2 for regex groups)',
    },
    {
      name: 'useRegex',
      type: 'boolean',
      label: 'Use Regex',
      defaultValue: false,
      description: 'Treat find pattern as regular expression',
    },
    {
      name: 'caseSensitive',
      type: 'boolean',
      label: 'Case Sensitive',
      defaultValue: true,
      description: 'Match case when searching',
    },
    {
      name: 'replaceAll',
      type: 'boolean',
      label: 'Replace All',
      defaultValue: true,
      description: 'Replace all occurrences',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const find = (config.find as string) || '';
    const replace = (config.replace as string) || '';
    const useRegex = config.useRegex as boolean;
    const caseSensitive = config.caseSensitive !== false;
    const replaceAll = config.replaceAll !== false;

    if (!find) {
      return input; // Nothing to find
    }

    let searchPattern: RegExp | string;

    if (useRegex) {
      try {
        const flags = (caseSensitive ? '' : 'i') + (replaceAll ? 'g' : '');
        searchPattern = new RegExp(find, flags);
      } catch (e) {
        throw ToolError.validation(`Invalid regex pattern: ${find}`);
      }
    } else {
      // Escape special regex characters for literal search
      const escaped = find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const flags = (caseSensitive ? '' : 'i') + (replaceAll ? 'g' : '');
      searchPattern = new RegExp(escaped, flags);
    }

    return input.replace(searchPattern, replace);
  },
};

